'use client'
import { Title } from '@/components/common'
import { useCompareStore, useWishlistStore } from '@/store'
import { getVND } from '@/utils/helpers'
import { Box, Stack, styled, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useTranslations } from 'next-intl'
import Link from '@/features/ref/components/Link'
import { Fragment, useMemo, useState } from 'react'
import { CardProductProps, ProductSelectedType } from '../../types'
import { AddToCartButton, WishlistButton } from '../action-button'
import { CardWrapProduct } from './CardWrapProduct'
import { OptionsPopup } from './OptionsPopup'
import { RatingProduct } from './Rating'

const StyledButton = styled(Button)(() => {
  return {
    minHeight: 36.5,
    p: { xs: '6px 10px', sm: '6px 12px', md: '6px 16px' },
    width: '100%',
    marginTop: 8,
  }
})

export const CardProduct = ({
  data,
  direction = 'GRID',
  dataWishlist,
  customAddToCartBtn,
  size = 'default',
  responsive = false,
  hiddenBtn,
}: CardProductProps) => {
  const t = useTranslations('Product')
  const dataPrice = data?.price_range?.maximum_price
  const defaultProduct: IVariantsProduct = useMemo(
    () =>
      data?.variants?.find((it) => it?.product?.stock_status === 'IN_STOCK') ||
      data?.variants?.[0] ||
      ({} as any),
    [data?.variants]
  )
  const defaultOptions = defaultProduct?.attributes?.reduce(
    (acc, el) => ({ ...acc, [el?.code]: el?.uid }),
    {}
  ) as Record<string, string>

  const [productSelected, setProductSelected] = useState<ProductSelectedType>({
    product: defaultProduct,
    options: defaultOptions,
    parent: data,
  })
  const [showOptionsPopup, setShowOptionsPopup] = useState(false)
  const [loading, setLoading] = useState(false)

  const { wishlistItems } = useWishlistStore()
  const { compareList } = useCompareStore()

  const currentPrice = useMemo(() => {
    return productSelected?.product?.product?.price_range?.maximum_price || dataPrice
  }, [productSelected, dataPrice])

  const isWishlistItem =
    dataWishlist ??
    wishlistItems?.items?.find(
      (item) => item?.configured_variant?.sku == (productSelected?.product?.product?.sku ?? data?.sku)
    )

  const isCompared = compareList?.some(
    (el) =>
      el?.product?.uid ==
      (isWishlistItem?.configured_variant?.uid ??
        isWishlistItem?.product?.uid ??
        productSelected?.product?.product?.uid ??
        data?.uid)
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataProduct: Record<string, string> = useMemo(() => defaultOptions, [])

  const getBadge = () => {
    const badge = []
    data?.stock_status && badge.push(data?.stock_status)
    currentPrice?.discount?.amount_off > 0 && badge.push(currentPrice?.discount?.amount_off)
    return badge
  }

  const urlWishlist = dataWishlist?.configurable_options?.reduce(
    (acc, cur) => {
      const key = cur?.option_label?.toLowerCase()
      acc[key] = cur?.configurable_product_option_value_uid
      return acc
    },
    {} as Record<string, string>
  )

  const url_link = `/products/${data?.url_key}?${new URLSearchParams(urlWishlist ?? productSelected?.options)}`

  const badge = getBadge().map((el, index) => (
    <span
      key={index}
      className={`rounded-sm px-2 py-1 text-xs font-semibold ${el?.toString()?.toLowerCase()?.includes('out_of_stock') ? 'bg-red-500 text-white' : 'bg-[#CBD5E1] text-[#334155]'}`}
    >
      {typeof el == 'string' ? t(el as any) : el}
    </span>
  ))

  const changeProduct = (value: string, type: string) => {
    dataProduct[type] = value
    const product = data?.variants?.find((el: any) =>
      Object.entries(dataProduct).every((option) =>
        el?.attributes?.find((attribute: any) => attribute?.code == option[0] && attribute?.uid == option[1])
      )
    )
    setProductSelected({ product: product, options: dataProduct, parent: data })
  }

  const handleAddToCartClick = () => {
    if (
      direction === 'GRID' &&
      data?.configurable_product_options_selection?.configurable_options?.length > 0
    ) {
      setShowOptionsPopup(true)
    }
  }

  const handleClosePopup = () => {
    setShowOptionsPopup(false)
  }

  const renderCategories = () => {
    return (
      <Typography
        variant="body2"
        sx={{ color: 'secondary.main', overflow: 'hidden', textOverflow: 'ellipsis', textWrap: 'nowrap' }}
      >
        {data?.categories?.length > 0 &&
          data?.categories
            ?.map((el) => {
              return el?.name
            })
            ?.join(', ')}
      </Typography>
    )
  }

  const renderCommon = () => {
    return (
      <Fragment>
        {renderCategories()}
        <Typography
          variant="body1"
          style={{ fontWeight: 600 }}
          sx={(theme) => ({
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [theme.breakpoints.down('md')]: {
              margin: '0 !important',
            },
          })}
        >
          <Link href={url_link} className="text-black">
            <span
              dangerouslySetInnerHTML={{
                __html: dataWishlist?.configured_variant?.name ?? dataWishlist?.product?.name ?? data?.name,
              }}
            ></span>
          </Link>
        </Typography>
        <RatingProduct rating={data?.rating_summary} review={data?.review_count} />
        {direction !== 'GRID' && (
          <div className="options-inline">
            {dataWishlist
              ? dataWishlist?.configurable_options?.map((el) => (
                  <div key={el?.configurable_product_option_uid} className="flex flex-wrap gap-2">
                    {el?.option_label?.toLocaleLowerCase() == 'color' ? (
                      <div
                        className="product-options-color active"
                        style={{ '--color': el?.value_label ?? '#0F172A' } as any}
                      ></div>
                    ) : (
                      <div className="active">{el?.value_label}</div>
                    )}
                  </div>
                ))
              : data?.configurable_options?.map((el) => (
                  <Stack direction={'row'} flexWrap={'wrap'} gap={1} key={el?.uid} sx={{ mb: 1 }}>
                    <Box className="w-full text-sm font-medium">{el?.label}:</Box>
                    {el?.values?.map((item) => {
                      if (el?.attribute_code == 'color')
                        return (
                          <div
                            key={item?.uid}
                            className={`product-options-color cursor-pointer ${item?.uid == productSelected?.options?.[el?.attribute_code] ? 'active' : ''}`}
                            style={{ '--color': item?.swatch_data?.value ?? '#0F172A' } as any}
                            onClick={() => changeProduct(item?.uid, el?.attribute_code)}
                          ></div>
                        )
                      return (
                        <div
                          key={item?.uid}
                          className={`cursor-pointer rounded border px-3 py-1 ${item?.uid == productSelected?.options?.[el?.attribute_code] ? 'active border-primary bg-primary text-white' : 'border-gray-300 hover:border-primary'}`}
                          onClick={() => changeProduct(item?.uid, el?.attribute_code)}
                        >
                          {item?.label}
                        </div>
                      )
                    })}
                  </Stack>
                ))}
          </div>
        )}
      </Fragment>
    )
  }

  const renderContentBtnAddToCart = () => {
    if (loading) return <CircularProgress size={20} sx={{ color: 'white' }} />
    return (
      <Typography
        variant="body1"
        sx={{ color: 'white', fontSize: { xs: '12px!important', sm: '1rem!important' } }}
      >
        {t('add-to-cart')}
      </Typography>
    )
  }

  const convertedProductSelectedData = useMemo(() => {
    try {
      if (Object.keys(productSelected?.product || {}).length > 0) {
        return productSelected?.product
      }
      if (Object.keys(defaultProduct).length > 0) {
        return defaultProduct
      }
      return { product: data }
    } catch (error) {}
  }, [data, defaultProduct, productSelected])

  return (
    <CardWrapProduct
      direction={direction}
      responsive={responsive}
      img={{
        link: url_link,
        src:
          dataWishlist?.configured_variant?.image?.url ??
          dataWishlist?.product?.small_image?.url ??
          productSelected?.product?.product?.small_image?.url ??
          data?.image?.url,
        size: size,
      }}
      badge={badge}
    >
      {direction == 'GRID' ? (
        <div className="mt-auto flex h-full gap-x-2 divide-y-2">
          <Stack sx={{ flex: 1, overflow: 'hidden' }} justifyContent={'space-between'}>
            {renderCommon()}
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {getVND(currentPrice?.final_price?.value)}
              {currentPrice?.discount?.amount_off > 0 && (
                <span className="text-sub-color ps-2 text-xs line-through">
                  {getVND(currentPrice?.regular_price?.value)}
                </span>
              )}
            </Typography>
            {data?.configurable_options?.length > 0 ? (
              <StyledButton variant="contained" onClick={handleAddToCartClick}>
                {renderContentBtnAddToCart()}
              </StyledButton>
            ) : (
              <AddToCartButton
                data={data}
                customAddToCartBtn={customAddToCartBtn}
                setLocalLoading={setLoading}
                productSelected={[
                  { ...productSelected, product: convertedProductSelectedData as IVariantsProduct },
                ]}
                dataWishlist={isWishlistItem}
                customRender={<StyledButton variant="contained">{renderContentBtnAddToCart()}</StyledButton>}
              />
            )}
          </Stack>
        </div>
      ) : (
        <div className={`flex gap-x-2 ${responsive ? 'flex-col' : 'divide-x-2'}`}>
          <div
            className={`flex w-full flex-1 flex-col justify-between ${responsive ? '' : 'overflow-hidden'}`}
          >
            <div className={`flex flex-wrap gap-2`}>
              {badge?.map((el, index) => (
                <div className="rounded-sm" key={index}>
                  {el}
                </div>
              ))}
            </div>
            {renderCommon()}
            <div
              className="spacing-bottom-3 line-clamp-2"
              dangerouslySetInnerHTML={{ __html: data?.short_description?.html ?? '' }}
            ></div>
          </div>
          <Stack
            direction="column"
            justifyContent="flex-end"
            sx={{
              ...(responsive ? {} : { flexBasis: 256, paddingLeft: 2.5 }),
            }}
          >
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {getVND(currentPrice?.final_price?.value)}
              {currentPrice?.discount?.amount_off > 0 && (
                <span className="text-sub-color ps-2 text-xs line-through">
                  {getVND(currentPrice?.regular_price?.value)}
                </span>
              )}
            </Typography>
            <div className="space-y-2">
              {/* {!hiddenBtn?.includes('compare') && (
                <div
                  className={`min-h-6 cursor-pointer transition-all ${isCompared ? 'text-green-400' : 'text-blue-500'}`}
                >
                  {isCompared ? (
                    <div className="flex items-center gap-x-1">
                      <Image width={16} height={16} src={'/assets/images/icon_done.svg'} alt="" />
                      in compare list{' '}
                      <AnotherButton
                        data={data}
                        productSelected={productSelected}
                        isCompared={isCompared}
                        customRender={
                          <div className="cursor-pointer text-xs leading-none text-gray-400 underline hover:text-primary">
                            Remove
                          </div>
                        }
                      />
                    </div>
                  ) : (
                    <AnotherButton
                      data={data}
                      productSelected={productSelected}
                      isCompared={isCompared}
                      customRender={
                        <div className="cursor-pointer leading-none hover:underline">
                          + add to compare list
                        </div>
                      }
                    />
                  )}
                </div>
              )} */}

              <AddToCartButton
                data={data}
                customAddToCartBtn={customAddToCartBtn}
                productSelected={[
                  { ...productSelected, product: convertedProductSelectedData as IVariantsProduct },
                ]}
                setLocalLoading={setLoading}
                customRender={<StyledButton variant="contained">{renderContentBtnAddToCart()}</StyledButton>}
              />
              {!hiddenBtn?.includes('wishlist') && (
                <WishlistButton
                  customRender={
                    <Button
                      variant="outlined"
                      sx={{ minHeight: 38 }}
                      className={`w-full ${!!isWishlistItem ? 'wishlist-selected' : ''}`}
                    >
                      {!!isWishlistItem ? 'Remove item from wishlist' : 'Add to wishlist'}
                    </Button>
                  }
                  isWishList={!!isWishlistItem}
                  data={data}
                  productSelected={productSelected}
                  dataWishlist={isWishlistItem}
                />
              )}
            </div>
          </Stack>
        </div>
      )}

      {/* Options Selection Popup */}
      <OptionsPopup
        loading={loading}
        setLoading={setLoading}
        currentPrice={currentPrice}
        show={showOptionsPopup}
        onClose={handleClosePopup}
        data={data}
        productSelected={productSelected}
        onChangeProduct={changeProduct}
        dataWishlist={isWishlistItem}
        customAddToCartBtn={customAddToCartBtn}
      />
    </CardWrapProduct>
  )
}
